import { DEFAULT_COACH_NAME_FALLBACK, FormerCoachEntry } from '@cf-shared/data-model/coach';

/**
 * Phase 3: on re-claim of the same coach, drop the matching `formerCoaches`
 * entry so the picker's "Former coach" badge stops rendering for this coach's
 * UDEs and the active-coach hide-rule reactivates. Pure so it can be
 * unit-tested without Angular DI. Returns either:
 *  - the same input array reference when nothing matched (no allocation);
 *  - `undefined` when the filter removed every entry (caller stores undefined
 *    rather than an empty array to keep the doc shape minimal);
 *  - a new filtered array when one or more entries were removed.
 */
export function removeFormerCoachByUid(
  formerCoaches: FormerCoachEntry[] | undefined,
  coachUid: string,
): FormerCoachEntry[] | undefined {
  if (!formerCoaches || formerCoaches.length === 0) return formerCoaches;
  const filtered = formerCoaches.filter(e => e.coachUid !== coachUid);
  if (filtered.length === formerCoaches.length) return formerCoaches;
  return filtered.length > 0 ? filtered : undefined;
}

/** Which sentence the claim screen shows under its title (#6793). */
export enum CoachInvitationWording {
  /** the coach filled the intake, so the program exists before the client accepts */
  ProgramPrepared = 'program-prepared',
  /** the coach sent a blank intake: the client answers the questions, then the coach builds the program */
  BlankIntake = 'blank-intake',
}

/**
 * Picks the claim screen's sentence from the server's `clientFills`. An older server never
 * sends it, and every invite it knows about reads the same as before this field existed,
 * so absent means the coach-filled wording.
 */
export function decideCoachInvitationWording(clientFills: boolean | undefined): CoachInvitationWording {
  return clientFills ? CoachInvitationWording.BlankIntake : CoachInvitationWording.ProgramPrepared;
}

/**
 * The name the claim sentence starts with. It is the coach's own free-text account name,
 * printed as typed: the sentence no longer puts "Coach" in front, because a name that
 * already starts with it read "Coach Coach …" (#6793). An empty name falls back to "Coach"
 * so the sentence never starts with a blank.
 */
export function coachInvitationName(coachName: string | undefined): string {
  return coachName?.trim() || DEFAULT_COACH_NAME_FALLBACK;
}
